import { useEffect, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import {
    Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '@/components/ui/dialog';
import { Plus, Pencil, Trash2, Bell, Loader2 } from 'lucide-react';
import api from '@/lib/api';

const TYPES = [
    { value: 'class_reminder', label: 'Recordatorio de clase (X min antes)' },
    { value: 'water_reminder', label: 'Recordatorio de agua (horario)' },
    { value: 'promotion', label: 'Promoción (1 vez por día)' },
    { value: 'subscription_reminder', label: 'Recordatorio de tarjeta/suscripción (X días antes)' },
    { value: 'owner_no_classes_reminder', label: 'Aviso a dueños: sin clases programadas (horario)' },
];

const TYPE_DESCRIPTIONS = {
    class_reminder: 'Se envía a los inscriptos en clases que queden X minutos antes del inicio.',
    water_reminder: 'Se envía a usuarios con el recordatorio de agua activo, a la hora configurada.',
    promotion: 'Se envía una vez por día a todos los suscriptores del gimnasio (o todos).',
    subscription_reminder: 'Se envía a usuarios cuya tarjeta vence dentro de X días.',
    owner_no_classes_reminder: 'Se envía a los dueños cuyo gimnasio no tiene clases programadas, a la hora configurada.',
};

export default function AdminNotifications() {
    const [items, setItems] = useState([]);
    const [gyms, setGyms] = useState([]);
    const [loading, setLoading] = useState(true);
    const [dialogOpen, setDialogOpen] = useState(false);
    const [editing, setEditing] = useState(null);
    const [form, setForm] = useState({
        title: '',
        body: '',
        type: 'class_reminder',
        gym_id: '',
        minutes_before: '180',
        config_time: '',
        config_days_before: '5',
        enabled: true,
    });
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState('');

    const fetchData = async () => {
        setLoading(true);
        try {
            const res = await api.get('/admin/scheduled-notifications');
            setItems(res.data.notifications || []);
            setGyms(res.data.gyms || []);
        } catch {}
        finally { setLoading(false); }
    };

    useEffect(() => { fetchData(); }, []);

    const openCreate = () => {
        setEditing(null);
        setForm({ title: '', body: '', type: 'class_reminder', gym_id: '', minutes_before: '180', config_time: '', config_days_before: '5', enabled: true });
        setError('');
        setDialogOpen(true);
    };

    const openEdit = (item) => {
        setEditing(item);
        setForm({
            title: item.title || '',
            body: item.body || '',
            type: item.type || 'class_reminder',
            gym_id: item.gym_id ? String(item.gym_id) : '',
            minutes_before: String(item.minutes_before ?? '180'),
            config_time: item.config?.time || '',
            config_days_before: String(item.config?.days_before || '5'),
            enabled: item.enabled !== false,
        });
        setError('');
        setDialogOpen(true);
    };

    const buildPayload = () => {
        const config = {};
        if (form.type === 'water_reminder' || form.type === 'promotion' || form.type === 'subscription_reminder' || form.type === 'owner_no_classes_reminder') {
            if (form.config_time) config.time = form.config_time;
        }
        if (form.type === 'owner_no_classes_reminder' && !config.time) {
            config.time = '07:00';
        }
        if (form.type === 'subscription_reminder') {
            config.days_before = parseInt(form.config_days_before) || 5;
        }
        return {
            title: form.title,
            body: form.body,
            type: form.type,
            gym_id: form.gym_id ? parseInt(form.gym_id) : null,
            minutes_before: parseInt(form.minutes_before) || 180,
            enabled: !!form.enabled,
            config: Object.keys(config).length > 0 ? config : null,
        };
    };

    const handleSave = async (e) => {
        e.preventDefault();
        setSaving(true);
        setError('');
        try {
            const data = buildPayload();
            if (editing) {
                await api.put(`/admin/scheduled-notifications/${editing.id}`, data);
            } else {
                await api.post('/admin/scheduled-notifications', data);
            }
            setDialogOpen(false);
            fetchData();
        } catch (err) {
            setError(err.response?.data?.message || 'Error al guardar.');
        } finally { setSaving(false); }
    };

    const handleDelete = async (id) => {
        if (!window.confirm('¿Eliminar esta regla de notificación?')) return;
        try {
            await api.delete(`/admin/scheduled-notifications/${id}`);
            fetchData();
        } catch {}
    };

    const handleToggleEnabled = async (item) => {
        try {
            await api.put(`/admin/scheduled-notifications/${item.id}`, {
                title: item.title,
                body: item.body,
                type: item.type,
                gym_id: item.gym_id,
                minutes_before: item.minutes_before,
                enabled: !item.enabled,
                config: item.config,
            });
            fetchData();
        } catch {}
    };

    const typeLabel = (t) => TYPES.find((x) => x.value === t)?.label || t;

    return (
        <div className="max-w-4xl mx-auto space-y-6">
            <div className="flex items-center justify-between">
                <h1 className="text-2xl font-bold uppercase tracking-wide text-white">Notificaciones programadas</h1>
                <Button onClick={openCreate}>
                    <Plus className="h-4 w-4 mr-2" /> Nueva regla
                </Button>
            </div>

            {loading && (
                <div className="flex items-center gap-2 text-white/60">
                    <Loader2 className="h-5 w-5 animate-spin" />
                    <span>Cargando...</span>
                </div>
            )}

            {!loading && items.length === 0 && (
                <p className="text-white/60">No hay reglas de notificación configuradas.</p>
            )}

            <div className="space-y-4">
                {items.map((item) => (
                    <Card key={item.id} className={`bg-[#0a0a0a] border-hairline-dark ${!item.enabled ? 'opacity-60' : ''}`}>
                        <CardContent className="p-4">
                            <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                                <div className="flex items-start gap-3">
                                    <div className="h-8 w-8 bg-white/10 rounded-full flex items-center justify-center shrink-0">
                                        <Bell className="h-4 w-4" />
                                    </div>
                                    <div>
                                        <h3 className="text-sm font-semibold text-white">{item.title}</h3>
                                        <p className="text-xs text-white/60 line-clamp-1">{item.body}</p>
                                        <div className="flex items-center gap-2 mt-1 text-xs text-white/50">
                                            <span>{typeLabel(item.type)}</span>
                                            <span className="text-white/20">|</span>
                                            <span>{item.gym ? item.gym.name : 'Todos los gimnasios'}</span>
                                            {item.config?.time && (
                                                <>
                                                    <span className="text-white/20">|</span>
                                                    <span>🕐 {item.config.time}</span>
                                                </>
                                            )}
                                            {item.config?.days_before && (
                                                <>
                                                    <span className="text-white/20">|</span>
                                                    <span>{item.config.days_before} días antes</span>
                                                </>
                                            )}
                                        </div>
                                    </div>
                                </div>
                                <div className="flex items-center gap-3">
                                    <Switch
                                        checked={item.enabled}
                                        onCheckedChange={() => handleToggleEnabled(item)}
                                    />
                                    <Button size="icon" variant="ghost" onClick={() => openEdit(item)}>
                                        <Pencil className="h-4 w-4" />
                                    </Button>
                                    <Button size="icon" variant="ghost" className="text-red-400" onClick={() => handleDelete(item.id)}>
                                        <Trash2 className="h-4 w-4" />
                                    </Button>
                                </div>
                            </div>
                        </CardContent>
                    </Card>
                ))}
            </div>

            <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
                <DialogContent className="bg-[#0a0a0a] border-hairline-dark text-white max-w-lg">
                    <DialogHeader>
                        <DialogTitle className="text-lg font-bold">
                            {editing ? 'Editar regla' : 'Nueva regla de notificación'}
                        </DialogTitle>
                    </DialogHeader>
                    <form onSubmit={handleSave} className="space-y-4">
                        <div>
                            <Label className="text-white">Tipo</Label>
                            <select
                                value={form.type}
                                onChange={(e) => setForm({ ...form, type: e.target.value })}
                                className="w-full h-9 px-2 rounded-md bg-black border border-hairline-dark text-white text-sm focus:outline-none focus:ring-1 focus:ring-white/20"
                            >
                                {TYPES.map((t) => (
                                    <option key={t.value} value={t.value}>{t.label}</option>
                                ))}
                            </select>
                            <p className="text-[11px] text-white/40 mt-1">{TYPE_DESCRIPTIONS[form.type]}</p>
                        </div>
                        <div>
                            <Label className="text-white">Título</Label>
                            <Input
                                value={form.title}
                                onChange={(e) => setForm({ ...form, title: e.target.value })}
                                className="bg-black border-hairline-dark text-white"
                                required
                            />
                        </div>
                        <div>
                            <Label className="text-white">Mensaje</Label>
                            <Textarea
                                value={form.body}
                                onChange={(e) => setForm({ ...form, body: e.target.value })}
                                className="bg-black border-hairline-dark text-white min-h-[80px]"
                                required
                            />
                        </div>
                        <div className="grid grid-cols-2 gap-4">
                            {form.type === 'class_reminder' && (
                                <div>
                                    <Label className="text-white">Minutos antes</Label>
                                    <Input
                                        type="number"
                                        min={1}
                                        max={2880}
                                        value={form.minutes_before}
                                        onChange={(e) => setForm({ ...form, minutes_before: e.target.value })}
                                        className="bg-black border-hairline-dark text-white"
                                        required
                                    />
                                    <p className="text-[10px] text-white/40 mt-1">Por defecto 180 = 3 horas</p>
                                </div>
                            )}
                            {form.type === 'water_reminder' && (
                                <div>
                                    <Label className="text-white">Horario de envío</Label>
                                    <Input
                                        type="time"
                                        value={form.config_time}
                                        onChange={(e) => setForm({ ...form, config_time: e.target.value })}
                                        className="bg-black border-hairline-dark text-white"
                                        required
                                    />
                                    <p className="text-[10px] text-white/40 mt-1">Ej: 10:00 — se envía a esa hora todos los días</p>
                                </div>
                            )}
                            {form.type === 'promotion' && (
                                <div>
                                    <Label className="text-white">Horario (opcional)</Label>
                                    <Input
                                        type="time"
                                        value={form.config_time}
                                        onChange={(e) => setForm({ ...form, config_time: e.target.value })}
                                        className="bg-black border-hairline-dark text-white"
                                    />
                                    <p className="text-[10px] text-white/40 mt-1">Si se deja vacío, se envía en el primer cron del día</p>
                                </div>
                            )}
                            {form.type === 'subscription_reminder' && (
                                <>
                                    <div>
                                        <Label className="text-white">Días antes del vencimiento</Label>
                                        <Input
                                            type="number"
                                            min={1}
                                            max={60}
                                            value={form.config_days_before}
                                            onChange={(e) => setForm({ ...form, config_days_before: e.target.value })}
                                            className="bg-black border-hairline-dark text-white"
                                            required
                                        />
                                        <p className="text-[10px] text-white/40 mt-1">Por defecto 5 días</p>
                                    </div>
                                    <div>
                                        <Label className="text-white">Horario (opcional)</Label>
                                        <Input
                                            type="time"
                                            value={form.config_time}
                                            onChange={(e) => setForm({ ...form, config_time: e.target.value })}
                                            className="bg-black border-hairline-dark text-white"
                                        />
                                    </div>
                                </>
                            )}
                            {form.type === 'owner_no_classes_reminder' && (
                                <div>
                                    <Label className="text-white">Horario de envío</Label>
                                    <Input
                                        type="time"
                                        value={form.config_time || '07:00'}
                                        onChange={(e) => setForm({ ...form, config_time: e.target.value })}
                                        className="bg-black border-hairline-dark text-white"
                                        required
                                    />
                                    <p className="text-[10px] text-white/40 mt-1">Ej: 07:00 — se envía a esa hora todos los días</p>
                                </div>
                            )}
                            <div>
                                <Label className="text-white">Gimnasio</Label>
                                <select
                                    value={form.gym_id}
                                    onChange={(e) => setForm({ ...form, gym_id: e.target.value })}
                                    className="w-full h-9 px-2 rounded-md bg-black border border-hairline-dark text-white text-sm focus:outline-none focus:ring-1 focus:ring-white/20"
                                >
                                    <option value="">Todos</option>
                                    {gyms.map((g) => (
                                        <option key={g.id} value={g.id}>{g.name}</option>
                                    ))}
                                </select>
                            </div>
                        </div>
                        <div className="flex items-center gap-2">
                            <Switch
                                checked={form.enabled}
                                onCheckedChange={(v) => setForm({ ...form, enabled: v })}
                            />
                            <Label className="text-white text-sm">Activa</Label>
                        </div>
                        {error && <p className="text-sm text-red-400">{error}</p>}
                        <DialogFooter>
                            <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>Cancelar</Button>
                            <Button type="submit" disabled={saving}>
                                {saving ? 'Guardando...' : (editing ? 'Guardar cambios' : 'Crear')}
                            </Button>
                        </DialogFooter>
                    </form>
                </DialogContent>
            </Dialog>
        </div>
    );
}
